/* eslint-disable @typescript-eslint/no-unused-vars */
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';

import { AnalystAssignment } from '@scenario/models/analyst-assignment.model';
import { PsfUser } from '@scenario/models/psf-user.model';
import { RootState } from '@store/create-store';

export interface AnalystAddition {
  userId: number;
  sourceId: number;
  firstName?: string;
  lastName?: string;
  zoneIds: number[];
}

export interface AnalystState {
  analysts: AnalystAssignment[];
  users: PsfUser[];
  isFetching: boolean;
  isFetchingUsers: boolean;
  isSaving: boolean;
  error?: string;
}

const initialState: AnalystState = {
  analysts: [],
  users: [],
  isFetching: false,
  isFetchingUsers: false,
  isSaving: false,
};

export const analystSlice = createSlice({
  name: 'analyst',
  initialState,
  reducers: {
    retrieveAnalysts: state => {
      state.isFetching = true;
      state.error = undefined;
    },
    retrieveAnalystsSuccess: (state, action: PayloadAction<AnalystAssignment[]>) => {
      state.analysts = action.payload;
      state.isFetching = false;
    },
    retrieveAnalystsFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isFetching = false;
    },
    retrievePsfUsers: state => {
      state.isFetchingUsers = true;
    },
    retrievePsfUsersSuccess: (state, action: PayloadAction<PsfUser[]>) => {
      state.users = [...action.payload].sort((a, b) => a.email.localeCompare(b.email));
      state.isFetchingUsers = false;
    },
    retrievePsfUsersFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isFetchingUsers = false;
    },
    addAnalyst: (state, _action: PayloadAction<AnalystAddition>) => {
      state.isSaving = true;
      state.error = undefined;
    },
    removeAnalyst: (state, _action: PayloadAction<number>) => {
      state.isSaving = true;
      state.error = undefined;
    },
    saveAnalystSuccess: state => {
      state.isSaving = false;
    },
    saveAnalystFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isSaving = false;
    },
  },
});

export type AnalystViewModel = {
  analysts: AnalystAssignment[];
  users: PsfUser[];
  isFetching: boolean;
  isFetchingUsers: boolean;
  isSaving: boolean;
  error?: string;
};

export const selectAnalystViewModel = createSelector(
  (state: RootState) => state,
  (state): AnalystViewModel => ({
    analysts: state.analyst.analysts,
    users: state.analyst.users,
    isFetching: state.analyst.isFetching,
    isFetchingUsers: state.analyst.isFetchingUsers,
    isSaving: state.analyst.isSaving,
    error: state.analyst.error,
  }),
);

export const {
  addAnalyst,
  removeAnalyst,
  retrieveAnalysts,
  retrieveAnalystsFailure,
  retrieveAnalystsSuccess,
  retrievePsfUsers,
  retrievePsfUsersFailure,
  retrievePsfUsersSuccess,
  saveAnalystFailure,
  saveAnalystSuccess,
} = analystSlice.actions;

export default analystSlice.reducer;
